import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Divider
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  SwapHoriz as SwapHorizIcon,
  CheckCircle as CheckCircleIcon,
  Timeline as TimelineIcon,
  Support as SupportIcon,
  ExitToApp as ExitToAppIcon
} from '@mui/icons-material';
import DevSecOpsTabs from '../../components/devsecops/DevSecOpsTabs';
import { useNavigate } from 'react-router-dom';
import DevSecOpsComponentCard, { DevSecOpsComponentDto } from '../../components/devsecops/DevSecOpsComponentCard';
import api, { ApiResponse, authService, User } from '../../services/api';
import { devsecopsApi } from '../../services/devsecopsApi';

const DevSecOpsTransition: React.FC = () => {
  const navigate = useNavigate();
  const [components, setComponents] = useState<DevSecOpsComponentDto[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentUserRole, setCurrentUserRole] = useState<User['role'] | undefined>(undefined);

  const handleBack = () => {
    navigate('/devsecops');
  };

  const transitionInSteps = [
    {
      label: 'Phase 1: Planning & Preparation (Days 1-10)',
      activities: [
        'Review incumbent documentation and knowledge base',
        'Identify key stakeholders and establish communication channels',
        'Conduct initial meetings with Government POCs',
        'Develop detailed transition-in plan and schedule',
        'Submit Transition-In Plan deliverable'
      ]
    },
    {
      label: 'Phase 2: Knowledge Transfer (Days 11-30)',
      activities: [
        'Shadow incumbent team members on critical tasks',
        'Document current processes, procedures, and workflows',
        'Review codebase, infrastructure, and deployment processes',
        'Attend handoff meetings for all active projects',
        'Establish access to all required systems and tools'
      ]
    },
    {
      label: 'Phase 3: Operational Readiness (Days 31-60)',
      activities: [
        'Assume responsibility for day-to-day operations',
        'Conduct parallel operations with incumbent oversight',
        'Validate CI/CD pipelines and deployment procedures',
        'Complete security and compliance training',
        'Demonstrate operational capability to Government'
      ]
    },
    {
      label: 'Phase 4: Full Operational Capability (Days 61-90)',
      activities: [
        'Assume full operational responsibility',
        'Implement process improvements and automation',
        'Establish baseline metrics and reporting',
        'Complete transition-in activities and close-out',
        'Submit Transition-In Completion Report'
      ]
    }
  ];

  const ongoingSupportActivities = [
    'Continuous monitoring and alerting for all production systems',
    '24/7 incident response and escalation procedures',
    'Regular security patching and vulnerability remediation',
    'Performance optimization and capacity planning',
    'Bi-weekly sprint planning and retrospectives',
    'Monthly status reporting and stakeholder briefings',
    'Quarterly disaster recovery testing and validation',
    'Continuous improvement of CI/CD pipelines and automation'
  ];

  const transitionOutActivities = [
    {
      phase: '90 Days Before Contract End',
      tasks: [
        'Submit Transition-Out Plan to Government',
        'Identify all deliverables, documentation, and artifacts',
        'Begin comprehensive documentation updates',
        'Schedule knowledge transfer sessions'
      ]
    },
    {
      phase: '60 Days Before Contract End',
      tasks: [
        'Conduct initial knowledge transfer sessions with successor',
        'Provide access to all systems and repositories',
        'Document all operational procedures and runbooks',
        'Transfer ownership of monitoring and alerting systems'
      ]
    },
    {
      phase: '30 Days Before Contract End',
      tasks: [
        'Complete all knowledge transfer activities',
        'Validate successor operational readiness',
        'Archive all project documentation and artifacts',
        'Conduct final status briefing with Government'
      ]
    },
    {
      phase: 'Contract End',
      tasks: [
        'Transfer all remaining access and credentials',
        'Submit final deliverables and reports',
        'Complete contract close-out activities',
        'Provide 30-day post-contract support if required'
      ]
    }
  ];

  useEffect(() => {
    const fetchComponents = async () => {
      try {
        setLoading(true);
        const response = await devsecopsApi.get<ApiResponse<DevSecOpsComponentDto[]>>(
          '/devsecops/components',
          {
            params: { page: 'transition' }
          }
        );

        const data = response.data.data || [];
        setComponents(data);
        setError(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to load transition components';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    fetchComponents();
  }, []);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const user = await authService.getCurrentUser();
        setCurrentUserRole(user.role);
      } catch (_err) {
        // unauthenticated users will see read-only cards
      }
    };

    loadUser();
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to DevSecOps Program"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            Transition & Support
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Transition-In, Ongoing Support, and Transition-Out Strategy
          </Typography>
        </Box>
      </Box>

      {/* DevSecOps Tabs */}
      <DevSecOpsTabs activeTab="transition" />

      {/* Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Transition Strategy Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Our transition strategy ensures seamless continuity of operations throughout the contract lifecycle.
            We employ a phased approach to transition-in, maintain robust operational support throughout the
            period of performance, and execute a comprehensive transition-out to ensure zero disruption to
            USCIS mission-critical systems.
          </Typography>
        </CardContent>
      </Card>

      {/* Transition-In */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SwapHorizIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              Transition-In Plan (90-Day Ramp-Up)
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Our 90-day transition-in plan ensures comprehensive knowledge transfer, operational readiness,
            and seamless assumption of responsibilities from the incumbent contractor.
          </Typography>
          <Stepper orientation="vertical" sx={{ mt: 3 }}>
            {transitionInSteps.map((step, index) => (
              <Step key={index} active={true} completed={false}>
                <StepLabel>
                  <Typography variant="h6" fontWeight="bold">
                    {step.label}
                  </Typography>
                </StepLabel>
                <StepContent>
                  <List dense>
                    {step.activities.map((activity, idx) => (
                      <ListItem key={idx}>
                        <ListItemIcon>
                          <CheckCircleIcon fontSize="small" color="primary" />
                        </ListItemIcon>
                        <ListItemText primary={activity} />
                      </ListItem>
                    ))}
                  </List>
                </StepContent>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Ongoing Support */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <SupportIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              Ongoing Operational Support
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Throughout the period of performance, we provide comprehensive operational support to ensure
            system availability, security, and continuous improvement.
          </Typography>
          <Grid container spacing={2}>
            {ongoingSupportActivities.map((activity, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
                  <CheckCircleIcon color="success" sx={{ mr: 2, mt: 0.5 }} />
                  <Typography variant="body2">{activity}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Transition-Out */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ExitToAppIcon sx={{ color: 'primary.main', mr: 2, fontSize: 32 }} />
            <Typography variant="h5" color="primary" fontWeight="bold">
              Transition-Out Plan
            </Typography>
          </Box>
          <Typography variant="body1" paragraph>
            Our transition-out plan begins 90 days before contract end and ensures complete knowledge transfer,
            documentation, and operational continuity for the successor contractor.
          </Typography>
          {transitionOutActivities.map((phase, index) => (
            <Box key={index} sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
                {phase.phase}
              </Typography>
              <List dense>
                {phase.tasks.map((task, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      <TimelineIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={task} />
                  </ListItem>
                ))}
              </List>
              {index < transitionOutActivities.length - 1 && <Divider sx={{ my: 2 }} />}
            </Box>
          ))}
        </CardContent>
      </Card>

      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mb: 3 }}>
        Transition & Support Capabilities
      </Typography>

      {loading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading transition and support components...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" color="error" role="alert" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {components.map((component: DevSecOpsComponentDto) => (
          <Grid item xs={12} md={6} key={component.key}>
            <DevSecOpsComponentCard
              component={component}
              currentUserRole={currentUserRole}
              onUpdated={(updated) => {
                setComponents((prev: DevSecOpsComponentDto[]) =>
                  prev.map((c: DevSecOpsComponentDto) => (c.key === updated.key ? updated : c))
                );
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Container>
  );
};

export default DevSecOpsTransition;
